import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getMerchantById,
  getMerchantReviews,
  createFavorite,
  deleteFavorite,
  listFavorites,
  type ConsumerMerchantDetail,
  type PublicReview,
} from '@/lib/api';
import { formatPrice } from '@/lib/format';
import { pickName, pickDescription } from '@/lib/i18n-pick';
import { useTk, useLocale } from '@/lib/i18n';
import { useAuth } from '@/lib/use-auth';
import { trackBehavior } from '@/lib/track';
import { Button } from '@sabay/ui-kit';

interface LoadState {
  status: 'loading' | 'success' | 'error' | 'not-found';
  merchant: ConsumerMerchantDetail | null;
  errorMessage: string | null;
}

export function MerchantDetailPage(): React.ReactElement {
  const tk = useTk();
  const locale = useLocale();
  const navigate = useNavigate();
  const auth = useAuth();
  const { id } = useParams<{ id: string }>();
  const viewTrackedRef = useRef<string | null>(null);
  const [state, setState] = useState<LoadState>({
    status: 'loading',
    merchant: null,
    errorMessage: null,
  });
  const [favoriteId, setFavoriteId] = useState<string | null>(null);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  useEffect(() => {
    if (state.status !== 'success' || !id || !state.merchant?.id) return;
    if (auth.status !== 'authenticated' || !auth.token) return;
    if (state.merchant.id !== id) return;
    if (viewTrackedRef.current === id) return;
    viewTrackedRef.current = id;
    void trackBehavior('VIEW_MERCHANT', { merchantId: id }, auth.token);
  }, [state.status, state.merchant?.id, id, auth.status, auth.token]);

  useEffect(() => {
    if (!id) return;
    const ac = new AbortController();
    setState({ status: 'loading', merchant: null, errorMessage: null });
    getMerchantById(id, ac.signal)
      .then((res) =>
        setState({ status: 'success', merchant: res.merchant, errorMessage: null }),
      )
      .catch((err: Error & { code?: string }) => {
        if (ac.signal.aborted) return;
        if (err.code === 'MERCHANT_NOT_FOUND') {
          setState({ status: 'not-found', merchant: null, errorMessage: null });
        } else {
          setState({ status: 'error', merchant: null, errorMessage: err.message });
        }
      });
    return () => ac.abort();
  }, [id]);

  // Check if merchant is already favorited
  useEffect(() => {
    if (!id || auth.status !== 'authenticated' || !auth.token) return;
    const ac = new AbortController();
    listFavorites({ type: 'MERCHANT', pageSize: 100 }, auth.token, ac.signal)
      .then((res) => {
        const found = res.items.find((f) => f.targetId === id);
        if (found) setFavoriteId(found.id);
      })
      .catch(() => { /* silently ignore */ });
    return () => ac.abort();
  }, [id, auth.status, auth.token]);

  const handleToggleFavorite = useCallback(async () => {
    if (!id || auth.status !== 'authenticated' || !auth.token) return;
    setFavoriteLoading(true);
    const prev = favoriteId;

    if (favoriteId) {
      // Optimistic: remove
      setFavoriteId(null);
      try {
        await deleteFavorite(favoriteId, auth.token);
      } catch {
        // Rollback
        setFavoriteId(prev);
      }
    } else {
      // Optimistic: add (use a temp marker)
      setFavoriteId('__optimistic__');
      try {
        const res = await createFavorite({ targetType: 'MERCHANT', targetId: id }, auth.token);
        setFavoriteId(res.favorite.id);
      } catch {
        // Rollback
        setFavoriteId(null);
      }
    }
    setFavoriteLoading(false);
  }, [id, favoriteId, auth.status, auth.token]);

  return (
    <main className="min-h-screen px-6 py-6 space-y-4 pb-10">
      <header className="flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(-1)}
          className="p-0 text-sm text-[var(--tg-link)]"
          aria-label="back"
        >
          ←
        </Button>
        <span />
      </header>

      {state.status === 'loading' ? (
        <div className="text-center py-6 text-neutral-400 text-sm">
          {tk('common.loading')}
        </div>
      ) : null}

      {state.status === 'not-found' ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-neutral-500 text-sm">
          {tk('merchant.not_found')}
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="rounded-lg border border-danger-500 bg-danger-50 p-4 text-sm text-danger-700">
          <p>{tk('common.error.generic')}</p>
          <p className="text-xs opacity-75 mt-1 break-all">{state.errorMessage}</p>
        </div>
      ) : null}

      {state.status === 'success' && state.merchant ? (
        <>
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <h1 className="text-2xl font-semibold">
                {pickName(state.merchant, locale)}
              </h1>
              {auth.status === 'authenticated' ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleToggleFavorite}
                  disabled={favoriteLoading}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full p-0 transition-colors active:scale-90"
                  aria-label={
                    favoriteId
                      ? tk('merchant.action.favorited')
                      : tk('merchant.action.favorite')
                  }
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill={favoriteId ? '#EF4444' : 'none'}
                    stroke={favoriteId ? '#EF4444' : 'currentColor'}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </Button>
              ) : null}
            </div>
            <p className="text-xs text-[var(--tg-hint)]">
              {state.merchant.cityNameZh} · {state.merchant.categoryNameZh}
            </p>
          </section>

          {pickDescription(state.merchant.descriptionI18n, locale) ? (
            <section className="rounded-lg border border-neutral-200 bg-white p-4">
              <p className="text-sm text-neutral-700 whitespace-pre-wrap">
                {pickDescription(state.merchant.descriptionI18n, locale)}
              </p>
            </section>
          ) : null}

          <section className="rounded-lg border border-neutral-200 bg-white p-4 space-y-2 text-sm">
            {state.merchant.address ? (
              <Row label={tk('merchant.field.address')} value={state.merchant.address} />
            ) : null}
            {state.merchant.phone ? (
              <Row label={tk('merchant.field.phone')} value={state.merchant.phone} />
            ) : null}
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold">
              {tk('merchant.section.deals')}（{state.merchant.activeDeals.length}）
            </h2>
            {state.merchant.activeDeals.length === 0 ? (
              <div className="rounded-lg border border-dashed border-neutral-300 p-6 text-center text-neutral-500 text-sm">
                {tk('merchant.deals.empty')}
              </div>
            ) : (
              <ul className="space-y-3">
                {state.merchant.activeDeals.map((deal) => (
                  <li key={deal.id}>
                    <Link
                      to={`/groupons/${deal.id}`}
                      className="block rounded-lg border border-neutral-200 bg-white p-4 hover:border-brand-500"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium truncate">{deal.titleZh}</h3>
                          <p className="text-xs text-neutral-500 mt-1">
                            {tk('merchant.deals.sold')}: {deal.soldCount}
                          </p>
                        </div>
                        <div className="flex flex-col items-end shrink-0">
                          <span className="text-brand-500 font-semibold">
                            {formatPrice(deal.priceCents, deal.currency)}
                          </span>
                          <span className="text-xs text-neutral-400 line-through">
                            {formatPrice(deal.originalPriceCents, deal.currency)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <MerchantReviewsSection merchantId={state.merchant.id} />
        </>
      ) : null}
    </main>
  );
}

function MerchantReviewsSection({ merchantId }: { merchantId: string }): React.ReactElement {
  const tk = useTk();
  const [reviews, setReviews] = useState<{
    items: PublicReview[];
    total: number;
    averageRating: number | null;
  } | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    getMerchantReviews(merchantId, { pageSize: 10 }, ac.signal)
      .then((res) =>
        setReviews({
          items: res.items,
          total: res.total,
          averageRating: res.averageRating,
        }),
      )
      .catch(() => setReviews({ items: [], total: 0, averageRating: null }));
    return () => ac.abort();
  }, [merchantId]);

  if (!reviews) return <div className="text-xs text-neutral-400 text-center">…</div>;

  return (
    <section className="space-y-3">
      <div className="flex items-baseline gap-2">
        <h2 className="text-base font-semibold">
          {tk('merchant.section.reviews')}（{reviews.total}）
        </h2>
        {reviews.averageRating !== null ? (
          <span className="text-sm text-[var(--tg-hint)]">
            ★ {reviews.averageRating.toFixed(1)}
          </span>
        ) : null}
      </div>
      {reviews.items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-4 text-center text-neutral-500 text-sm">
          {tk('merchant.reviews.empty')}
        </div>
      ) : (
        <ul className="space-y-3">
          {reviews.items.map((r) => (
            <li key={r.id} className="rounded-lg border border-neutral-200 bg-white p-4 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="font-medium">{r.reviewerName ?? tk('merchant.reviews.anonymous')}</span>
                <span className="text-warning-500">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
              </div>
              <p className="text-neutral-700 mt-1 whitespace-pre-wrap break-words">{r.comment}</p>
              {r.publishedAt ? (
                <p className="text-xs text-[var(--tg-hint)] mt-1">
                  {new Date(r.publishedAt).toLocaleDateString()}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Row({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[var(--tg-hint)] shrink-0">{label}</span>
      <span className="text-right break-all">{value}</span>
    </div>
  );
}
